<template>
  <ExpandedSideSection
    :loading="applicationsStore.isLoadingSelected"
    :no-data="!selectedApplication && !applicationsStore.isLoadingSelected"
    :back-route="backRoute"
    empty-text="No application found"
  >
    <template #header-actions> </template>

    <!-- Application content -->
    <div class="p-4 bg-white border-[1px] rounded-lg border-gray-200">
      <!-- Job Main Data Component -->
      <div class="px-4 py-2 bg-gray-50 rounded-lg">
        <JobMainData
          :minimal="true"
          :role-data="selectedApplication?.open_role_details"
          :show-editable-status="false"
          :show-candidate-info="false"
          :show-date="false"
        />
      </div>

      <!-- Application data -->
      <div class="mt-4 px-2 rounded-lg flex justify-between items-center">
        <div class="p-1 cursor-default flex items-center text-gray-500 text-xs ml-auto">
          <Calendar class="w-4 h-4 mr-1" />
          Updated
          {{ selectedApplication?.updated_at ? formatDate(selectedApplication.updated_at) : '' }}
        </div>
      </div>

      <!-- Application Status Dropdowns -->
      <div v-if="selectedApplication && applicationID" class="p-4 bg-white rounded-lg">
        <UserApplicationStatuses
          :selected-application="selectedApplication"
          :application-id="applicationID"
          :disabled="false"
          @status-updated="() => {}"
        />
      </div>

      <div class="mt-4">
        <h3 class="text-l mb-4">Application details</h3>
        <div class="p-4 bg-white rounded-lg border-[1px] border-gray-200">
              <Form v-model="mainForm" @submit="handleSubmit" class="space-y-4">
                <!-- Referrer fields -->
                <FormField v-if="isGuide" name="referrer">
                  <FormItem>
                    <FormLabel class="font-semibold flex items-center">
                      <UserRoundCheck class="w-4 h-4 mr-2" />Referrer:
                    </FormLabel>
                    <FormControl>
                      <Select v-model="mainForm.referrer" :disabled="referrersStore.isLoading">
                        <SelectTrigger>
                          <SelectValue placeholder="Select referrer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup v-for="group in groupedReferrers" :key="group.label">
                            <SelectLabel>{{ group.label }}</SelectLabel>
                            <SelectItem
                              v-for="item in group.items"
                              :key="item.value ?? 'null'"
                              :value="item.value"
                              :disabled="!!item.disabled"
                            >
                              {{ item.label }}
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>

                <FormField v-if="isGuide" name="recruiters">
                  <FormItem>
                    <FormLabel class="font-semibold flex items-center">
                      <MailCheck class="w-4 h-4 mr-2" />Recruiter(s):
                    </FormLabel>
                    <FormControl>
                      <Select
                        v-model="mainForm.recruiters"
                        multiple
                        :disabled="recruitersStore.isLoading"
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select recruiters" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem
                              v-for="recruiter in recruitersStore.recruiters"
                              :key="recruiter.id"
                              :value="recruiter.id"
                            >
                              {{ recruiter.name }}
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <div class="flex flex-wrap gap-1 mt-1">
                      <span
                        v-for="recruiter in mainForm?.recruiters ?? []"
                        :key="recruiter"
                        class="text-xs bg-gray-100 rounded-full px-2 py-1 font-normal flex items-center gap-1"
                      >
                        {{ recruitersStore.recruiters.find((object) => object.id === recruiter)?.name }}
                        <button
                          @click="
                            mainForm.recruiters = mainForm.recruiters.filter((id) => id !== recruiter)
                          "
                          class="hover:text-red-500 transition-colors ml-1"
                          type="button"
                        >
                          ×
                        </button>
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                </FormField>

                <FormField name="comments">
                  <FormItem>
                    <FormLabel class="font-semibold flex items-center">
                      <MessageSquareMore class="w-4 h-4 mr-2" />Comments:
                    </FormLabel>
                    <FormControl>
                      <Textarea v-model="mainForm.comments" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </FormField>
              </Form>

          <!-- Timeline -->
          <TimelineForm v-model="timelineForm" />
        </div>
      </div>

      <!-- Fixed bottom buttons -->
      <div class="flex items-center justify-end gap-2 h-12 px-4 border-t border-gray-200 flex-shrink-0 mt-4 bg-white rounded-b-lg">
        <Button
          @click="deleteApplication"
          variant="destructive"
          class="mr-auto bg-transparent border-red-500 border-[1px] text-red-500 hover:text-white text-xs h-fit py-2"
        >
          Delete application
        </Button>
        <Button @click="goBack" variant="outline" class="w-20 text-xs h-fit py-2">Cancel</Button>
        <Button
          @click="handleSubmit"
          :disabled="!selectedApplication?.open_role_details?.status_open"
          class="w-20 bg-cosmicPurple hover:bg-cosmicPurple/80 hover:translate-y-[-1px] transition-all duration-200 text-xs h-fit py-2"
          :class="{ 'opacity-50': !selectedApplication?.open_role_details?.status_open }"
          :title="!selectedApplication?.open_role_details?.status_open ? 'Role is closed' : ''"
        >
          {{ applicationsStore.isLoadingSelected ? 'Saving...' : 'Save' }}
        </Button>
      </div>

    </div>

    <div
      v-if="applicationsStore.error"
      class="block w-full mt-4 text-red-500 text-sm text-right px-4"
    >
      {{ applicationsStore.error }}
    </div>

    <div
      v-if="!selectedApplication?.open_role_details?.status_open"
      class="w-full mt-4 text-sm text-right flex items-center justify-end text-red-500 px-4"
    >
      <TriangleAlert class="w-4 h-4 inline mr-1" /> Application cannot be edited because the role
      has been closed
    </div>

    <UserApplicationDelete
      v-if="deleting"
      :application-id="applicationID"
      @close="deleting = false"
      :all-applications="props.allApplications"
    />
  </ExpandedSideSection>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  MessageSquareMore,
  TriangleAlert,
  UserRoundCheck,
  MailCheck,
  Calendar
} from 'lucide-vue-next'
import { useApplicationsStore } from '@/stores/applications'
import UserApplicationDelete from './UserApplicationDelete.vue'
import TimelineForm from '../common/TimelineForm.vue'
import JobMainData from '@/components/common/JobMainData.vue'
import UserApplicationStatuses from '@/components/userApplications/UserApplicationStatuses.vue'
import ExpandedSideSection from '@/components/common/ExpandedSideSection.vue'
import { useAuth } from '@/composables/useAuth'
import { useReferrersStore } from '@/stores/referrers'
import { useRecruitersStore } from '@/stores/recruiters'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/formatDate'

const props = defineProps<{
  applicationId: string
  allApplications: boolean
}>()

const { isGuide } = useAuth()

const route = useRoute()
const router = useRouter()

const applicationsStore = useApplicationsStore()
const applicationID = ref(route.params.application_id as string)
const selectedApplication = computed(() => applicationsStore.selectedApplication)

const backRoute = computed(() => {
  return {
    name: props.allApplications
      ? 'All Applications Expanded Application'
      : 'User Application container',
    params: { application_id: selectedApplication.value?.id }
  }
})

const referrersStore = useReferrersStore()
const recruitersStore = useRecruitersStore()
const deleting = ref(false)

const mainForm = ref({
  comments: '',
  referrer: null as number | null,
  recruiters: [] as number[]
})


// Update the form values when the selectedApplication changes
watch(
  () => selectedApplication.value,
  (newVal) => {
    if (newVal) {
      mainForm.value = {
        comments: newVal.comments ?? '',
        referrer: newVal.referrer_details?.id ?? null,
        recruiters: (newVal.recruiters ?? []).map((r) => r.id)
      }
    }
  },
  { immediate: true }
)

const timelineForm = ref({
  date_offer_signed: undefined as Date | undefined,
  date_offer_received: undefined as Date | undefined,
  date_onsite_result: undefined as Date | undefined,
  date_onsite_invited: undefined as Date | undefined,
  date_first_call: undefined as Date | undefined,
  date_coding_test: undefined as Date | undefined,
  date_referral_confirmed: undefined as Date | undefined,
  date_referral_requested: undefined as Date | undefined
})

// Update timeline form when selectedApplication changes
watch(
  () => selectedApplication.value,
  (newVal) => {
    if (newVal) {
      timelineForm.value = {
        date_offer_signed: newVal.date_offer_signed
          ? new Date(newVal.date_offer_signed)
          : undefined,
        date_offer_received: newVal.date_offer_received
          ? new Date(newVal.date_offer_received)
          : undefined,
        date_onsite_result: newVal.date_onsite_result
          ? new Date(newVal.date_onsite_result)
          : undefined,
        date_onsite_invited: newVal.date_onsite_invited
          ? new Date(newVal.date_onsite_invited)
          : undefined,
        date_first_call: newVal.date_first_call ? new Date(newVal.date_first_call) : undefined,
        date_coding_test: newVal.date_coding_test ? new Date(newVal.date_coding_test) : undefined,
        date_referral_confirmed: newVal.date_referral_confirmed
          ? new Date(newVal.date_referral_confirmed)
          : undefined,
        date_referral_requested: newVal.date_referral_requested
          ? new Date(newVal.date_referral_requested)
          : undefined
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  try {
    await applicationsStore.fetchApplicationById(applicationID.value)
    await referrersStore.fetchReferrers()
    await recruitersStore.fetchRecruiters()
  } catch (error) {
    console.error('Failed to fetch application:', error)
  }
})

const groupedReferrers = computed(() => {
  const referrers = referrersStore.referrers || []
  const employerId = selectedApplication.value?.open_role_details?.employer
  const employerName = selectedApplication.value?.open_role_details?.employer_details?.name

  const activeReferrers = referrers.filter((referrer) => referrer.is_active)

  const companyRefs = activeReferrers
    .filter((referrer) => referrer.employers?.includes(employerId as number))
    .map((referrer) => ({
      label: referrer.name,
      value: referrer.id,
      disabled: false
    }))

  const otherRefs = activeReferrers
    .filter((referrer) => !referrer.employers?.includes(employerId as number))
    .map((referrer) => ({
      label: referrer.name,
      value: referrer.id,
      disabled: false
    }))

  return [
    {
      label: `Referrers at ${employerName}`,
      items: companyRefs.length
        ? companyRefs
        : [{ label: `No ${employerName} referrers available`, value: null, disabled: true }]
    },
    {
      label: 'Other Referrers',
      items: otherRefs.length
        ? otherRefs
        : [{ label: 'No other referrers available', value: null, disabled: true }]
    }
  ]
})

async function handleSubmit() {
  try {
    const payload = {
      ...mainForm.value,
      ...Object.fromEntries(
        Object.entries(timelineForm.value).map(([key, value]) => [
          key,
          value instanceof Date ? value.toISOString().split('T')[0] : null
        ])
      )
    }
    await applicationsStore.updateApplication(applicationID.value, payload as any)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(
      props.allApplications
        ? {
            name: 'All Applications Expanded Application',
            params: { application_id: applicationID.value }
          }
        : { name: 'User Application container', params: { application_id: applicationID.value } }
    )
  } catch (error) {
    console.error('Failed to update application:', error)
  }
}

const deleteApplication = async () => {
  deleting.value = true
}

const goBack = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(
    props.allApplications
      ? {
          name: 'All Applications Expanded Application',
          params: { application_id: applicationID.value }
        }
      : { name: 'User Application container', params: { application_id: applicationID.value } }
  )
}
</script>

<style></style>
